import EntryLink from '@databases/content-link/EntryLink';
import { ViewComponentProps } from '@databases/fields/view/types';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { BacklinkDisplayType, DatabaseFieldType } from '@databases/types/database';
import { getBacklinkEntries } from '@databases/utils/database/field-types/backlink';
import { getDatabaseEntryRequired, getEntryName } from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import React, { FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';
import { getSelectDatabaseDraft } from '../../database-value/utils';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;

  > li + li {
    margin-top: 6px;
  }
`;

const preventDefault = (e: React.MouseEvent | React.FocusEvent) => {
  // Prevent propagation to inline edit
  e.stopPropagation();
};

const BacklinkValueView: FunctionComponent<
  ViewComponentProps<DatabaseFieldType.BACKLINK>
> = ({ contentId, field, entryId, noFocus, isReadonly }) => {
  const scope = useScope();
  const dispatch = defaultStoreUtils.useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;

  useCallbackEffect(
    useFlaggingCallback(async () => {
      if (field.settings.contentId) {
        await dispatch(loadDatabaseByContentId(field.settings.contentId, intl));
      }
    }, [dispatch, field.settings.contentId, intl])
  );

  const [targetEntryRefs] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const entry = entryId && getDatabaseEntryRequired(databaseDraft, entryId);
    const targetEntries = entry
      ? getBacklinkEntries({ database: databaseDraft, field, entry }, state, {
          selectDatabase: getSelectDatabaseDraft(scope, true),
        })
      : [];

    return [
      targetEntries.map((targetEntry) => {
        const entryName = getEntryName(targetEntry, state);

        return {
          databaseSpaceKey: targetEntry.database.space.key,
          databaseId: targetEntry.database.id,
          contentId: targetEntry.database.contentId,
          entryId: targetEntry.entry.id,
          title: entryName
            ? entryName
            : formatMessage(messages['unnamed-entities.unnamed-entry']),
        };
      }),
    ] as const;
  });

  switch (field.settings.backlinkDisplayType) {
    case BacklinkDisplayType.NUMBER:
      return <>{targetEntryRefs.length}</>;

    default: {
      return (
        <Container>
          {targetEntryRefs.map((entryRef) => (
            <li key={entryRef.entryId}>
              <EntryLink
                entryRef={entryRef}
                noFocus={noFocus}
                onClick={preventDefault}
                onFocus={preventDefault}
                contentId={contentId}
                isReadonly={isReadonly}
              />
            </li>
          ))}
        </Container>
      );
    }
  }
};

export default BacklinkValueView;
